<template>
  <!-- [matrah] Header / body / footer / buttons editor for one template language. -->
  <div class="lang-editor">
    <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
      <LanguageFlag :code="modelValue.code" :size="22" />
      <span class="text-subtitle-1 font-weight-medium">{{ meta.name }}</span>
      <span class="text-medium-emphasis">({{ meta.native }})</span>
      <v-chip size="x-small" variant="tonal" color="primary">{{ meta.dir.toUpperCase() }}</v-chip>
      <v-spacer />
      <v-menu v-if="copySources.length">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" size="small" variant="tonal" color="primary" prepend-icon="mdi-content-copy">
            Copy from
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item v-for="src in copySources" :key="src.code" @click="$emit('copy-from', src.code)">
            <template v-slot:prepend>
              <LanguageFlag :code="src.code" :size="18" />
            </template>
            <v-list-item-title class="ml-2">{{ src.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- Header -->
    <v-card variant="outlined" class="section-card mb-4">
      <v-card-title class="section-title">
        <v-icon icon="mdi-format-header-1" size="18" class="mr-1" /> Header
        <span class="text-caption text-medium-emphasis ml-2">optional</span>
      </v-card-title>
      <v-card-text>
        <v-btn-toggle :model-value="modelValue.header.type" @update:model-value="setHeaderType" mandatory divided
          color="primary" variant="outlined" density="comfortable" class="mb-3 header-toggle">
          <v-btn v-for="t in headerTypes" :key="t.value" :value="t.value" size="small">
            <v-icon :icon="t.icon" size="16" class="mr-1" /> {{ t.title }}
          </v-btn>
        </v-btn-toggle>

        <v-text-field v-if="modelValue.header.type === 'TEXT'" :model-value="modelValue.header.text"
          @update:model-value="patchHeader({ text: $event })" label="Header text" variant="outlined" density="comfortable"
          :counter="limits.headerTextMax" :maxlength="limits.headerTextMax" :dir="meta.dir"
          hint="Plain text only, no variables" persistent-hint />

        <v-text-field v-else-if="isMediaHeader" :model-value="modelValue.header.example_url"
          @update:model-value="patchHeader({ example_url: $event })" :label="mediaLabel + ' example URL'"
          variant="outlined" density="comfortable" prepend-inner-icon="mdi-link-variant" placeholder="https://…"
          :hint="'Public HTTPS link to a sample ' + mediaLabel.toLowerCase() + '. Meta reviews it; the real file is chosen when sending.'"
          persistent-hint />
      </v-card-text>
    </v-card>

    <!-- Body -->
    <v-card variant="outlined" class="section-card mb-4">
      <v-card-title class="section-title">
        <v-icon icon="mdi-text-long" size="18" class="mr-1" /> Body
        <span class="text-caption text-error ml-2">required</span>
      </v-card-title>
      <v-card-text>
        <div class="editor-toolbar mb-2">
          <v-btn-group density="comfortable" variant="outlined" divided>
            <v-btn size="small" title="Bold (*text*)" @click="wrapSelection('*')"><v-icon icon="mdi-format-bold" /></v-btn>
            <v-btn size="small" title="Italic (_text_)" @click="wrapSelection('_')"><v-icon icon="mdi-format-italic" /></v-btn>
            <v-btn size="small" title="Strikethrough (~text~)" @click="wrapSelection('~')"><v-icon icon="mdi-format-strikethrough-variant" /></v-btn>
          </v-btn-group>
          <v-btn size="small" color="secondary" variant="flat" prepend-icon="mdi-code-braces" class="ml-2"
            @click="insertVariable">
            Insert variable {{ nextVariableToken }}
          </v-btn>
        </div>

        <v-textarea ref="bodyField" :model-value="modelValue.body" @update:model-value="patch({ body: $event })"
          label="Message body" variant="outlined" rows="6" auto-grow :counter="limits.bodyMax"
          :maxlength="limits.bodyMax" :dir="meta.dir" :placeholder="bodyPlaceholder" />

        <div v-if="variables.length" class="mt-2">
          <div class="text-caption text-medium-emphasis mb-1">Sample values (Meta needs one example per variable)</div>
          <v-row dense>
            <v-col v-for="n in variables" :key="n" cols="12" sm="6">
              <v-text-field :model-value="(modelValue.body_examples || [])[n - 1] || ''"
                @update:model-value="setExample(n, $event)" :label="'Sample for {{' + n + '}}'" variant="outlined"
                density="compact" :dir="meta.dir" />
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>

    <!-- Footer -->
    <v-card variant="outlined" class="section-card mb-4">
      <v-card-title class="section-title">
        <v-icon icon="mdi-page-layout-footer" size="18" class="mr-1" /> Footer
        <span class="text-caption text-medium-emphasis ml-2">optional</span>
      </v-card-title>
      <v-card-text>
        <v-text-field :model-value="modelValue.footer" @update:model-value="patch({ footer: $event })"
          label="Footer text" variant="outlined" density="comfortable" :counter="limits.footerMax"
          :maxlength="limits.footerMax" :dir="meta.dir" />
      </v-card-text>
    </v-card>

    <!-- Buttons -->
    <v-card variant="outlined" class="section-card">
      <v-card-title class="section-title">
        <v-icon icon="mdi-gesture-tap-button" size="18" class="mr-1" /> Buttons
        <span class="text-caption text-medium-emphasis ml-2">
          {{ modelValue.buttons.length }}/{{ limits.buttonsMax }} · quick replies {{ counts.QUICK_REPLY }}/{{ limits.quickReplyMax }}
          · URL {{ counts.URL }}/{{ limits.urlMax_count }} · phone {{ counts.PHONE_NUMBER }}/{{ limits.phoneMax_count }}
        </span>
        <v-spacer />
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" size="small" color="primary" variant="tonal" prepend-icon="mdi-plus"
              :disabled="modelValue.buttons.length >= limits.buttonsMax">
              Add button
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item v-for="t in buttonTypes" :key="t.value" :disabled="!canAdd(t.value)" @click="addButton(t.value)"
              :prepend-icon="t.icon" :title="t.title" />
          </v-list>
        </v-menu>
      </v-card-title>
      <v-card-text>
        <div v-if="!modelValue.buttons.length" class="text-medium-emphasis text-body-2">
          No buttons. Add quick replies (bot flow choices) or call-to-action buttons (website / phone).
        </div>
        <v-card v-for="(btn, i) in modelValue.buttons" :key="i" variant="tonal" class="button-row mb-2" color="surface-light">
          <v-card-text class="pa-3">
            <v-row dense align="center">
              <v-col cols="12" sm="3">
                <v-select :items="buttonTypes" item-title="title" item-value="value" :model-value="btn.type"
                  @update:model-value="patchButton(i, { type: $event })" label="Type" variant="outlined" density="compact"
                  hide-details />
              </v-col>
              <v-col cols="12" :sm="btn.type === 'QUICK_REPLY' ? 8 : 4">
                <v-text-field :model-value="btn.text" @update:model-value="patchButton(i, { text: $event })"
                  label="Button text" variant="outlined" density="compact" :maxlength="limits.buttonTextMax"
                  :counter="limits.buttonTextMax" :dir="meta.dir" />
              </v-col>
              <v-col v-if="btn.type === 'URL'" cols="12" sm="4">
                <v-text-field :model-value="btn.url" @update:model-value="patchButton(i, { url: $event })"
                  label="URL" placeholder="https://…" variant="outlined" density="compact" :maxlength="limits.urlMax"
                  hide-details />
              </v-col>
              <v-col v-if="btn.type === 'PHONE_NUMBER'" cols="12" sm="4">
                <v-text-field :model-value="btn.phone_number" @update:model-value="patchButton(i, { phone_number: $event })"
                  label="Phone number" placeholder="+968…" variant="outlined" density="compact"
                  :maxlength="limits.phoneMax" hide-details />
              </v-col>
              <v-col cols="12" sm="1" class="d-flex justify-end button-actions">
                <v-btn icon="mdi-chevron-up" size="x-small" variant="text" :disabled="i === 0" @click="moveButton(i, -1)" title="Move up" />
                <v-btn icon="mdi-chevron-down" size="x-small" variant="text" :disabled="i === modelValue.buttons.length - 1" @click="moveButton(i, 1)" title="Move down" />
                <v-btn icon="mdi-delete-outline" size="x-small" variant="text" color="error" @click="removeButton(i)" title="Remove" />
              </v-col>
              <template v-if="btn.type === 'URL'">
                <v-col cols="12" sm="4" class="pt-0">
                  <v-switch :model-value="!!btn.dynamic" @update:model-value="patchButton(i, { dynamic: $event })"
                    :label="dynamicSuffixLabel" color="primary" density="compact" hide-details inset />
                </v-col>
                <v-col v-if="btn.dynamic" cols="12" sm="8" class="pt-0">
                  <v-text-field :model-value="btn.example" @update:model-value="patchButton(i, { example: $event })"
                    label="Example full URL (with the suffix filled in)" variant="outlined" density="compact"
                    placeholder="https://example.com/pay/abc123" hide-details />
                  <div class="text-caption text-medium-emphasis mt-1">
                    Sent as <code>{{ (btn.url || 'https://…') + dynamicToken }}</code>; the suffix is supplied per message.
                  </div>
                </v-col>
              </template>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <v-alert v-if="errors.length" type="error" variant="tonal" density="compact" class="mt-4">
      <ul class="pl-4">
        <li v-for="(e, i) in errors" :key="i">{{ e }}</li>
      </ul>
    </v-alert>

    <!-- Sample-value prompt shown right after inserting a variable -->
    <v-dialog v-model="sampleDialog.open" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Sample value for {{ sampleToken }}</v-card-title>
        <v-card-text>
          <p class="text-body-2 text-medium-emphasis mb-3">
            Meta reviews templates with a realistic example. What will this variable usually contain?
          </p>
          <v-text-field v-model="sampleDialog.value" label="Example" variant="outlined" density="comfortable" autofocus
            :dir="meta.dir" @keyup.enter="saveSample" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="sampleDialog.open = false">Later</v-btn>
          <v-btn color="primary" variant="flat" @click="saveSample">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import LanguageFlag from './LanguageFlag.vue'
import {
  BUTTON_TYPES,
  HEADER_TYPES,
  META_LIMITS,
  extractVariables,
  languageMeta,
} from '@/utils/templateLanguages'

export default {
  name: 'TemplateLanguageEditor',
  components: { LanguageFlag },
  props: {
    modelValue: { type: Object, required: true },
    copySources: { type: Array, default: () => [] },
    errors: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue', 'copy-from'],
  data: () => ({
    headerTypes: HEADER_TYPES,
    buttonTypes: BUTTON_TYPES,
    limits: META_LIMITS,
    sampleDialog: { open: false, index: 1, value: '' },
    bodyPlaceholder: 'Write the message. Use *bold*, _italic_, ~strike~ and {{1}} for dynamic values.',
    dynamicSuffixLabel: 'Dynamic suffix {{1}}',
    dynamicToken: '{{1}}',
  }),
  computed: {
    meta() {
      return languageMeta(this.modelValue.code)
    },
    isMediaHeader() {
      return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(this.modelValue.header.type)
    },
    mediaLabel() {
      return { IMAGE: 'Image', VIDEO: 'Video', DOCUMENT: 'Document' }[this.modelValue.header.type] || 'Media'
    },
    variables() {
      const found = extractVariables(this.modelValue.body)
      const max = found.length ? Math.max(...found) : 0
      return Array.from({ length: max }, (_, i) => i + 1)
    },
    nextVariableIndex() {
      return this.variables.length + 1
    },
    // tokens are built in JS because `{{` cannot appear inside a template interpolation
    nextVariableToken() {
      return `{{${this.nextVariableIndex}}}`
    },
    sampleToken() {
      return `{{${this.sampleDialog.index}}}`
    },
    counts() {
      const c = { QUICK_REPLY: 0, URL: 0, PHONE_NUMBER: 0 }
      this.modelValue.buttons.forEach((b) => {
        if (c[b.type] !== undefined) c[b.type] += 1
      })
      return c
    },
  },
  methods: {
    patch(partial) {
      this.$emit('update:modelValue', { ...this.modelValue, ...partial })
    },
    patchHeader(partial) {
      this.patch({ header: { ...this.modelValue.header, ...partial } })
    },
    setHeaderType(type) {
      if (!type) return
      this.patchHeader({ type, text: type === 'TEXT' ? this.modelValue.header.text : '', example_url: ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(type) ? this.modelValue.header.example_url : '' })
    },
    setExample(n, value) {
      const examples = [...(this.modelValue.body_examples || [])]
      while (examples.length < n) examples.push('')
      examples[n - 1] = value
      this.patch({ body_examples: examples })
    },
    textareaEl() {
      const root = this.$refs.bodyField?.$el
      return root ? root.querySelector('textarea') : null
    },
    wrapSelection(mark) {
      const el = this.textareaEl()
      const text = this.modelValue.body || ''
      let start = text.length
      let end = text.length
      if (el) {
        start = el.selectionStart ?? start
        end = el.selectionEnd ?? end
      }
      const selected = text.slice(start, end) || 'text'
      const next = text.slice(0, start) + mark + selected + mark + text.slice(end)
      this.patch({ body: next })
      this.$nextTick(() => {
        if (!el) return
        el.focus()
        el.setSelectionRange(start + 1, start + 1 + selected.length)
      })
    },
    insertVariable() {
      const n = this.nextVariableIndex
      const token = `{{${n}}}`
      const el = this.textareaEl()
      const text = this.modelValue.body || ''
      let pos = text.length
      if (el && typeof el.selectionStart === 'number') pos = el.selectionStart
      const before = text.slice(0, pos)
      const after = text.slice(pos)
      const spacer = before && !/\s$/.test(before) ? ' ' : ''
      const next = before + spacer + token + after
      this.patch({ body: next })
      this.sampleDialog = { open: true, index: n, value: (this.modelValue.body_examples || [])[n - 1] || '' }
      this.$nextTick(() => {
        if (!el) return
        const caret = (before + spacer + token).length
        el.setSelectionRange(caret, caret)
      })
    },
    saveSample() {
      this.setExample(this.sampleDialog.index, this.sampleDialog.value)
      this.sampleDialog.open = false
      this.$nextTick(() => this.textareaEl()?.focus())
    },
    canAdd(type) {
      if (this.modelValue.buttons.length >= this.limits.buttonsMax) return false
      const max = {
        QUICK_REPLY: this.limits.quickReplyMax,
        URL: this.limits.urlMax_count,
        PHONE_NUMBER: this.limits.phoneMax_count,
      }[type]
      return this.counts[type] < max
    },
    addButton(type) {
      if (!this.canAdd(type)) return
      const buttons = [...this.modelValue.buttons, { type, text: '', url: '', dynamic: false, example: '', phone_number: '' }]
      this.patch({ buttons })
    },
    patchButton(i, partial) {
      const buttons = this.modelValue.buttons.map((b, idx) => (idx === i ? { ...b, ...partial } : b))
      if (partial.type && !this.typeChangeAllowed(i, partial.type)) return
      this.patch({ buttons })
    },
    typeChangeAllowed(i, type) {
      const current = this.modelValue.buttons[i].type
      if (current === type) return true
      const max = {
        QUICK_REPLY: this.limits.quickReplyMax,
        URL: this.limits.urlMax_count,
        PHONE_NUMBER: this.limits.phoneMax_count,
      }[type]
      return this.counts[type] < max
    },
    removeButton(i) {
      this.patch({ buttons: this.modelValue.buttons.filter((_, idx) => idx !== i) })
    },
    moveButton(i, delta) {
      const j = i + delta
      if (j < 0 || j >= this.modelValue.buttons.length) return
      const buttons = [...this.modelValue.buttons]
      const tmp = buttons[i]
      buttons[i] = buttons[j]
      buttons[j] = tmp
      this.patch({ buttons })
    },
  },
}
</script>

<style scoped>
.section-card {
  border-color: #dfe6e0;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  padding: 10px 16px 4px;
  flex-wrap: wrap;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.header-toggle {
  flex-wrap: wrap;
  height: auto;
}

.button-actions {
  gap: 0;
}

code {
  background: #f1f4f1;
  padding: 1px 4px;
  border-radius: 3px;
}
</style>
